<template>
  <div class="chart-wizard">
    <div class="chart-wizard__card">
      <!-- Model (Dataset) -->
      <div class="chart-wizard__field">
        <label class="chart-wizard__label">Modelo</label>
        <v-select
          v-model="localDataset"
          :options="datasetOptions"
          :reduce="opt => opt.value"
          :clearable="false"
          placeholder="Seleccionar modelo"
          label="label"
          class="chart-wizard__select"
          append-to-body
          :calculate-position="withPopper"
        />
      </div>

      <!-- Date Range -->
      <div v-if="showTimeRange" class="chart-wizard__field">
        <label class="chart-wizard__label">Rango de fechas</label>
        <div class="chart-wizard__chips">
          <button
            v-for="preset in datePresets"
            :key="preset.value"
            type="button"
            class="chart-wizard__chip"
            :class="{ 'chart-wizard__chip--active': selectedDatePreset === preset.value }"
            @click="selectDatePreset(preset.value)"
          >
            {{ preset.label }}
          </button>
          <button
            type="button"
            class="chart-wizard__chip"
            :class="{ 'chart-wizard__chip--active': selectedDatePreset === 'custom' }"
            @click="toggleCalendar"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </button>
        </div>
        <div v-if="showCalendar" class="chart-wizard__calendar-row">
          <div class="chart-wizard__calendar-field">
            <label class="chart-wizard__sublabel">Desde</label>
            <flat-pickr
              v-model="customFrom"
              :config="flatpickrConfig"
              class="chart-wizard__input"
              placeholder="YYYY-MM-DD"
            />
          </div>
          <div class="chart-wizard__calendar-field">
            <label class="chart-wizard__sublabel">Hasta</label>
            <flat-pickr
              v-model="customTo"
              :config="flatpickrConfig"
              class="chart-wizard__input"
              placeholder="YYYY-MM-DD"
            />
          </div>
        </div>
      </div>

      <!-- Chart (View) -->
      <div class="chart-wizard__field">
        <label class="chart-wizard__label">Gráfica</label>
        <v-select
          v-model="localView"
          :options="viewOptions"
          :reduce="opt => opt.value"
          :clearable="false"
          :disabled="!localDataset"
          placeholder="Seleccionar gráfica"
          label="label"
          class="chart-wizard__select"
          append-to-body
          :calculate-position="withPopper"
        />
      </div>

      <!-- Continue -->
      <button
        class="chart-wizard__btn"
        :disabled="!canContinue"
        @click="onContinue"
      >
        Continuar
      </button>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import popperHelper from '@/libs/popper-helper'
import { getDatasetDefaultFrom, getDatasetModule } from '../config/datasets'
import { DEFAULT_TIME_RANGE_FROM, OPEN_TIME_RANGE_FROM } from '../utils/timeUtils'

export default {
  name: 'ChartSetupWizard',

  components: { vSelect, flatPickr },

  props: {
    datasetOptions: { type: Array, default: () => [] },
    viewOptions: { type: Array, default: () => [] },
    dateGroupingPresets: { type: Array, default: () => [] },
    t: { type: Function, required: true },
  },

  setup(props, { emit }) {
    const localDataset = ref('')
    const localView = ref('')
    const localFreq = ref('')

    // Date preset state
    const selectedDatePreset = ref(DEFAULT_TIME_RANGE_FROM)
    const showCalendar = ref(false)
    const customFrom = ref('')
    const customTo = ref('')

    const datePresets = [
      { value: 'now-15m', label: '15m' },
      { value: 'now-1h',  label: '1h' },
      { value: 'now-6h',  label: '6h' },
      { value: 'now-24h', label: '24h' },
      { value: 'now-7d',  label: '7d' },
      { value: 'now-30d', label: '30d' },
      { value: 'now-90d', label: '90d' },
      { value: OPEN_TIME_RANGE_FROM, label: 'Todo' },
    ]

    const flatpickrConfig = {
      dateFormat: 'Y-m-d',
      allowInput: true,
    }

    const selectDatePreset = (value) => {
      selectedDatePreset.value = value
      showCalendar.value = false
      customFrom.value = ''
      customTo.value = ''
    }

    const toggleCalendar = () => {
      showCalendar.value = !showCalendar.value
      if (showCalendar.value) {
        selectedDatePreset.value = 'custom'
      }
    }

    // Compute the resolved from/to values
    const resolvedFrom = computed(() => {
      if (selectedDatePreset.value === 'custom') {
        return customFrom.value || ''
      }
      return selectedDatePreset.value // e.g. 'now-30d'
    })

    const resolvedTo = computed(() => {
      if (selectedDatePreset.value === 'custom') {
        return customTo.value || ''
      }
      // For relative presets (now-30d, now-90d, etc.), 'to' is implicit — don't set it
      return ''
    })

    // A dataset whose time filter is unusable declares omitTimeRange: no date
    // controls, and its chart travels with no from/to (see useChartUrl).
    const showTimeRange = computed(() => !getDatasetModule(localDataset.value)?.omitTimeRange)

    // Reset view and freq when dataset changes
    watch(() => localDataset.value, () => {
      localView.value = ''
      localFreq.value = ''
      // Each dataset brings its own default window (getDatasetDefaultFrom).
      selectedDatePreset.value = getDatasetDefaultFrom(localDataset.value)
      showCalendar.value = false
      customFrom.value = ''
      customTo.value = ''
      emit('update:dataset', localDataset.value)
    })

    const canContinue = computed(() =>
      localDataset.value !== '' &&
      localView.value !== ''
    )

    const onContinue = () => {
      if (!canContinue.value) return
      emit('continue', {
        dataset: localDataset.value,
        view: localView.value,
        from: showTimeRange.value ? resolvedFrom.value : '',
        to: showTimeRange.value ? resolvedTo.value : '',
        freq: localFreq.value,
      })
    }

    return {
      localDataset,
      localView,
      localFreq,
      selectedDatePreset,
      showCalendar,
      customFrom,
      customTo,
      datePresets,
      flatpickrConfig,
      selectDatePreset,
      toggleCalendar,
      canContinue,
      showTimeRange,
      onContinue,
      withPopper: popperHelper.withPopper,
    }
  },
}
</script>

<style scoped>
.chart-wizard {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  padding: 24px;
}

.chart-wizard__card {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chart-wizard__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-wizard__label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
}

.chart-wizard__optional {
  font-weight: 400;
  text-transform: none;
  opacity: 0.5;
  font-size: 0.72rem;
}

.chart-wizard__sublabel {
  font-size: 0.72rem;
  font-weight: 500;
  opacity: 0.6;
}

.chart-wizard__select {
  font-size: 0.9rem;
}

/* ─── Chips ─── */
.chart-wizard__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.chart-wizard__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s;
}

.chart-wizard__chip:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.chart-wizard__chip--active {
  background: rgba(0, 104, 201, 0.25);
  border-color: rgba(0, 104, 201, 0.5);
  color: #5ba3e6;
}

/* ─── Calendar row ─── */
.chart-wizard__calendar-row {
  display: flex;
  gap: 12px;
  margin-top: 6px;
}

.chart-wizard__calendar-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chart-wizard__input {
  padding: 8px 12px;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  outline: none;
  transition: border-color 0.2s;
}

.chart-wizard__input:focus {
  border-color: #0068c9;
}

.chart-wizard__input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* ─── Continue button ─── */
.chart-wizard__btn {
  margin-top: 8px;
  padding: 10px 24px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  background: #02255a;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  align-self: flex-start;
}

.chart-wizard__btn:hover:not(:disabled) {
  background: #033a8a;
}

.chart-wizard__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ─── Light mode overrides ─── */
:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__input {
  background: #fff;
  border-color: #d0d0d0;
  color: #333;
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__input::placeholder {
  color: #aaa;
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__label {
  color: #555;
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__chip {
  border-color: rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.04);
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__chip:hover {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.2);
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-wizard__chip--active {
  background: rgba(0, 104, 201, 0.12);
  border-color: rgba(0, 104, 201, 0.4);
  color: #0068c9;
}
</style>
